import * as api from '../utils/api';
import { fetchAvailability, fetchPublicShop } from '../annuaire/tunnel/v2Api';
import { ANNUAIRE_ROOT } from '../annuaire/resolvePath';
import { jourDans } from '../annuaire/tunnel/creneaux';
import { MAX_SPAN_DAYS } from '../annuaire/tunnel/bookingRequest';

/**
 * FIXERLIGHT-052 lot 2 — ce qu'il faut pour proposer un nouveau créneau au
 * client, alors que `ClientWorkshopBookingResource` (contrat § 4) n'expose
 * délibérément ni l'id de fiche annuaire ni l'id du compte Fixer Lite
 * derrière un rendez-vous — seulement `shop.url`, la même adresse publique
 * qu'un visiteur de la fiche. Ce module refait donc, à partir de cette
 * seule URL, le même chemin que `WorkshopStandalone` au premier rendu
 * d'une fiche : `GET /shops/by-path/...` pour l'id, puis
 * `GET /v2/public/shops/{id}` pour savoir si l'atelier réserve en direct
 * (`instant_booking`, § 7 du contrat : ça change le message avant
 * confirmation, pas le choix du créneau lui-même).
 */

/**
 * @param {?string} shopUrl `booking.shop.url` — `null` quand la fiche n'est
 * pas publiée (contrat § 4) : on ne peut alors pas proposer de créneau, ce
 * n'est pas une erreur réseau.
 * @returns {?{dept: string, city: string, slug: string}} `null` si l'URL ne
 * correspond pas au format `/annuaire-velo/{dept}/{city}/{slug}`.
 */
export function parseAnnuairePath(shopUrl) {
  if (!shopUrl) return null;
  let pathname;
  try {
    pathname = new URL(shopUrl).pathname;
  } catch (e) {
    return null;
  }
  const prefix = `${ANNUAIRE_ROOT}/`;
  if (!pathname.startsWith(prefix)) return null;
  const segments = pathname.slice(prefix.length).split('/').filter(Boolean);
  if (segments.length !== 3) return null;
  const [dept, city, slug] = segments;
  return { dept, city, slug };
}

/**
 * @param {?string} shopUrl `booking.shop.url`.
 * @returns {Promise<?{shopId: number, instant: boolean}>} `null` quand la
 * fiche est absente ou que la résolution échoue (api en panne, fiche
 * dépubliée depuis) — l'appelant montre alors un état "indisponible" plutôt
 * que de planter.
 */
export async function resolveWorkshopBookingAccount(shopUrl) {
  const path = parseAnnuairePath(shopUrl);
  if (!path) return null;

  try {
    const shopItem = await api.get(
      `/shops/by-path/${path.dept}/${path.city}/${path.slug}`,
    );
    if (!shopItem || !shopItem.id) return null;

    const account = await fetchPublicShop(shopItem.id);
    return {
      shopId: shopItem.id,
      instant: !!(account && account.instant_booking),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Les créneaux proposables pour LE déplacement de ce rendez-vous : mêmes
 * prestations que la demande d'origine (`booking.requested`), dans l'espoir
 * que `duration_min` corresponde à la durée réelle du rendez-vous. Ce n'est
 * qu'une approximation : l'api recalcule cette durée à partir des
 * prestations ACTUELLES du compte (`durationFor`), qui peuvent avoir changé
 * ou été archivées depuis la prise du rendez-vous — l'appelant doit comparer
 * `duration_min` à `booking.duration_min` avant d'afficher les créneaux (une
 * divergence signifie qu'ils seraient refusés par `/reschedule`). Fenêtre de
 * `MAX_SPAN_DAYS` jours, la même que le tunnel de réservation.
 *
 * @param {number} shopId
 * @param {Array<{service_key: string}>} requested `booking.requested`.
 * @returns {Promise<{duration_min: ?number, slots: string[]}>}
 */
export function fetchWorkshopBookingSlots(shopId, requested) {
  const serviceKeys = (requested || [])
    .map((item) => item && item.service_key)
    .filter(Boolean);
  return fetchAvailability(
    shopId,
    jourDans(0),
    jourDans(MAX_SPAN_DAYS),
    serviceKeys.length ? serviceKeys : null,
  );
}
